"use client";

// ─────────────────────────────────────────────────────────────────────────────
// Interest & late-fee calculator — 234A / 234B / 234C / GST s.50 / GST late
// fee / TDS 201(1A) / 234E. All computation happens in the browser via the
// pure functions in ./_data/rules.ts; nothing is uploaded.
//
// WORKED EXAMPLES (hand-computed from the research formulas; the compute
// functions reproduce all three — verified by script on 19-Aug-2026, and the
// first of each is also this page's default inputs so the numbers are visible
// on load):
//
//   1. s.234A — tax on total income ₹5,00,000; advance tax ₹1,00,000; TDS/TCS
//      ₹1,50,000; no reliefs/MAT credit/SA tax. A = ₹2,50,000. Due 16-Sep-2025
//      (CBDT-extended date), filed 23-Dec-2025 → 3 full months + 7 days → 4
//      months. Interest = 1% × 4 × ₹2,50,000 = ₹10,000.
//
//   2. s.234C — tax due on returned income ₹1,00,000; cumulative advance tax
//      ₹10,000 / ₹30,000 / ₹60,000 / ₹90,000 by the four dates.
//      Q1: 12% tolerance (₹12,000) missed → 1%×3×₹5,000 = ₹150.
//      Q2: 36% tolerance (₹36,000) missed → 1%×3×₹15,000 = ₹450.
//      Q3: shortfall ₹15,000 → ₹450.  Q4: shortfall ₹10,000 → 1%×1 = ₹100.
//      Total ₹1,150.
//
//   3. s.201(1A) — the research's own example: TDS ₹10,000 deducted 21-May
//      (on time), deposit due 7-Jun, deposited 8-Jun. The 1.5% clock runs from
//      the DEDUCTION date: 21-May → 8-Jun touches May + June = 2 part-months
//      → 1.5% × 2 × ₹10,000 = ₹300.
// ─────────────────────────────────────────────────────────────────────────────

import { useId, useMemo, useState } from "react";

import { FaqJsonLd, ReviewedPageSchema } from "@/components/JsonLd";
import PageShell from "@/components/PageShell";

import {
  AATO_BANDS,
  type AatoBand,
  compute234A,
  compute234B,
  compute234C,
  compute234E,
  computeGst50_1,
  computeGst50_3,
  computeGstLateFee,
  type ComputeResult,
  computeTds201,
  getRule,
  type InterestRule,
  type Result234C,
  REVIEWED_ON,
  tdsDepositDueDate,
} from "./_data/rules";

// ── Formatting ───────────────────────────────────────────────────────────────

function inr(n: number, dp?: number): string {
  const decimals = dp ?? (Number.isInteger(n) ? 0 : 2);
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function fmtStep(amount: number, unit?: "inr" | "months" | "days"): string {
  if (unit === "months") return `${amount} month${amount === 1 ? "" : "s"}`;
  if (unit === "days") return `${amount} day${amount === 1 ? "" : "s"}`;
  return inr(amount);
}

function num(s: string): number {
  const v = parseFloat(s);
  return Number.isFinite(v) && v >= 0 ? v : 0;
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

type TabKey =
  | "234a"
  | "234b"
  | "234c"
  | "gst_interest"
  | "gst_late_fee"
  | "tds_201"
  | "234e";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "234a", label: "234A — late return" },
  { key: "234b", label: "234B — advance tax" },
  { key: "234c", label: "234C — instalments" },
  { key: "gst_interest", label: "GST interest (s.50)" },
  { key: "gst_late_fee", label: "GST late fee (s.47)" },
  { key: "tds_201", label: "TDS — 201(1A)" },
  { key: "234e", label: "234E — TDS statement" },
];

// ── FAQs (visible + emitted as FAQPage JSON-LD) ──────────────────────────────

const FAQS = [
  {
    q: "What is the difference between sections 234A, 234B and 234C?",
    a: "All three charge 1% simple interest per month, but for different defaults. 234A is for filing the return late — it runs on the net unpaid tax from the day after the due date to the filing date. 234B is for underpaying advance tax in aggregate — it applies only if advance tax plus TDS/TCS fell short of 90% of assessed tax, and runs from 1 April of the assessment year. 234C is for deferring the quarterly instalments — fixed 3-month interest on the shortfall of each of the first three instalments and 1 month on the 15-March shortfall, regardless of actual delay. They can all apply on the same return at once.",
  },
  {
    q: "How is a 'month or part of a month' counted under Rule 119A?",
    a: "Any fraction of a month counts as a full month — one day into a new month adds a whole month of interest. Rule 119A also rounds the interest base down to the nearest multiple of ₹100 before applying the rate. This month-rounding applies to income-tax interest (234A, 234B, 201(1A)); it does not apply to GST, where s.50 interest is computed day-wise, or to the per-day GST late fee and 234E fee.",
  },
  {
    q: "Is GST interest also charged per month?",
    a: "No. GST interest under CGST s.50 is computed per day on a per-annum basis under Rule 88B: amount × 18% × days ÷ 365. And for a belated self-assessed return, the net-cash rule means interest runs only on the portion of tax paid through the electronic cash ledger — the part settled by input tax credit attracts no interest, unless the return was filed after s.73/74/74A proceedings commenced.",
  },
  {
    q: "When does the 24% GST interest rate apply?",
    a: "In practice, never — after the Finance Act 2022 substitution, 24% survives only as the statutory ceiling in s.50(3). The notified rate for wrongly availed and utilised ITC is 18%, applied retrospectively from 1 July 2017 by Notification 09/2022-Central Tax. And s.50(3) interest arises only if the wrongly availed credit was actually utilised — mere availment, with the credit-ledger balance never dipping below the wrongly availed amount, attracts no interest at all.",
  },
  {
    q: "Why does depositing TDS one day late cost 3% interest?",
    a: "Because the 1.5%-per-month clock under s.201(1A) runs from the date of deduction, not from the deposit due date, and any part of a month counts as a full month. Deduct on 21 May, miss the 7 June due date and pay on 8 June: the period 21 May to 8 June touches two calendar months, so the interest is 1.5% × 2 = 3% of the TDS amount — for one day's delay past the due date.",
  },
  {
    q: "Can the ₹200-per-day fee under section 234E be waived?",
    a: "No. 234E is a fee, not a penalty — it is mandatory, there is no discretion to waive it, and it must be deposited before the TDS/TCS statement can be filed. Its only ceiling is the amount of TDS/TCS reported in that statement, so a nil-deduction statement attracts no fee. The separate penalty under s.271H (₹10,000 to ₹1,00,000, for defaults beyond one year or incorrect statements) is on top and is not something a calculator can compute.",
  },
  {
    q: "What are the GST late-fee caps for GSTR-3B and GSTR-1?",
    a: "Per return (CGST + SGST together), from the June-2021 tax period onward under Notifications 19/2021 and 20/2021-Central Tax: ₹500 for a NIL return; ₹2,000 where aggregate turnover in the preceding financial year was up to ₹1.5 crore; ₹5,000 for turnover above ₹1.5 crore and up to ₹5 crore; and ₹10,000 (the un-reduced statutory cap) above ₹5 crore. The per-day rate is ₹50 for a normal return and ₹20 for a NIL return. There is no late fee under the IGST Act.",
  },
  {
    q: "Do these section numbers change under the Income-tax Act, 2025?",
    a: "Yes, from tax year 2026-27: 234A becomes s.423, 234B becomes s.424, 234C becomes s.425, 201(1A) is consolidated into s.398 (together with the TCS mirror, old s.206C(7)), and 234E becomes s.427. The rates and mechanics carry over unchanged. GST is untouched by the Income-tax Act, 2025 — CGST s.47 and s.50 continue as they are.",
  },
];

// ── Small building blocks ────────────────────────────────────────────────────

const lbl: React.CSSProperties = {
  display: "block",
  fontSize: "10px",
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: "rgba(19,18,16,.5)",
  marginBottom: "6px",
  marginTop: "2px",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid var(--b200)",
  borderRadius: "4px",
  fontSize: "14px",
  background: "var(--b50)",
  outline: "none",
  color: "var(--o900)",
  fontFamily: "var(--font-dm), sans-serif",
  fontVariantNumeric: "tabular-nums",
  marginBottom: "16px",
};

function MoneyField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} style={lbl}>{label}</label>
      <input
        id={id}
        inputMode="decimal"
        style={inputStyle}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ""))}
      />
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} style={lbl}>{label}</label>
      <input
        id={id}
        style={inputStyle}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Segment<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ key: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        border: "1px solid var(--b200)",
        borderRadius: "4px",
        overflow: "hidden",
        marginBottom: "16px",
      }}
    >
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            style={{
              flex: 1,
              padding: "9px 10px",
              background: active ? "var(--o900)" : "var(--b50)",
              color: active ? "var(--b50)" : "var(--o900)",
              border: "none",
              cursor: "pointer",
              fontSize: "12px",
              fontFamily: "var(--font-dm), sans-serif",
              transition: "background .18s, color .18s",
            }}
            type="button"
            onClick={() => onChange(o.key)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function CheckField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        gap: "10px",
        alignItems: "flex-start",
        fontSize: "13px",
        color: "rgba(19,18,16,.7)",
        lineHeight: 1.6,
        marginBottom: "16px",
        cursor: "pointer",
      }}
    >
      <input
        checked={checked}
        style={{ marginTop: "3px", accentColor: "var(--cu)" }}
        type="checkbox"
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

// ── Result rendering ─────────────────────────────────────────────────────────

function TotalCard({
  heading,
  result,
  decimals,
}: {
  heading: string;
  result: ComputeResult;
  decimals?: number;
}) {
  return (
    <div
      style={{
        background: "var(--o900)",
        color: "var(--b50)",
        padding: "18px 22px",
        borderRadius: "6px 6px 0 0",
      }}
    >
      <div
        style={{
          fontSize: "10px",
          letterSpacing: ".14em",
          textTransform: "uppercase",
          color: "rgba(251,250,246,.55)",
          marginBottom: "4px",
        }}
      >
        {heading}
      </div>
      <div
        style={{
          fontFamily: "var(--font-fraunces), serif",
          fontSize: "32px",
          fontWeight: 200,
          letterSpacing: "-.02em",
        }}
      >
        {result.nil && result.total === 0 ? "Nil" : inr(result.total, decimals ?? 2)}
      </div>
    </div>
  );
}

function BreakdownTable({ result, decimals }: { result: ComputeResult; decimals?: number }) {
  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--b200)", borderTop: "none" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "480px" }}>
        <thead>
          <tr style={{ background: "var(--b100)" }}>
            {["Step", "Formula (numbers substituted)", "Value"].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: h === "Value" ? "right" : "left",
                  padding: "9px 14px",
                  fontSize: "10px",
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  color: "rgba(19,18,16,.5)",
                  fontWeight: 500,
                  borderBottom: "1px solid var(--b200)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.steps.map((s, i) => (
            <tr key={i}>
              <td
                style={{
                  padding: "10px 14px",
                  fontSize: "12px",
                  color: "var(--o900)",
                  borderBottom: "1px solid var(--b200)",
                  verticalAlign: "top",
                  maxWidth: "260px",
                }}
              >
                {s.label}
              </td>
              <td
                style={{
                  padding: "10px 14px",
                  fontSize: "12px",
                  color: "rgba(19,18,16,.6)",
                  borderBottom: "1px solid var(--b200)",
                  verticalAlign: "top",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {s.formula}
              </td>
              <td
                style={{
                  padding: "10px 14px",
                  fontSize: "13px",
                  color: "var(--o900)",
                  borderBottom: "1px solid var(--b200)",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                  verticalAlign: "top",
                }}
              >
                {s.unit ? fmtStep(s.amount, s.unit) : inr(s.amount, decimals ?? 2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Notes({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return (
    <ul
      style={{
        margin: "14px 0 0",
        paddingLeft: "18px",
        fontSize: "12px",
        color: "rgba(19,18,16,.6)",
        lineHeight: 1.75,
      }}
    >
      {notes.map((n, i) => (
        <li key={i} style={{ marginBottom: "6px" }}>
          {n}
        </li>
      ))}
    </ul>
  );
}

/** Citation pair + period basis + exceptions, rendered beside every result. */
function RulePanel({ rule }: { rule: InterestRule }) {
  return (
    <div
      style={{
        marginTop: "22px",
        border: "1px solid var(--b200)",
        borderRadius: "6px",
        background: "var(--b100)",
        padding: "18px 20px",
      }}
    >
      <div style={{ fontSize: "10px", letterSpacing: ".14em", textTransform: "uppercase", color: "rgba(19,18,16,.5)", marginBottom: "10px" }}>
        The rule being applied
      </div>
      <div style={{ display: "grid", gap: "10px" }}>
        <div style={{ fontSize: "12px", lineHeight: 1.7, color: "rgba(19,18,16,.7)" }}>
          <strong style={{ color: "var(--o900)" }}>Citation (as enacted):</strong> {rule.citationOld}
        </div>
        <div style={{ fontSize: "12px", lineHeight: 1.7, color: "rgba(19,18,16,.7)" }}>
          <strong style={{ color: "var(--o900)" }}>2025-Act / current equivalent:</strong> {rule.citationNew}
        </div>
        <div style={{ fontSize: "12px", lineHeight: 1.7, color: "rgba(19,18,16,.7)" }}>
          <strong style={{ color: "var(--o900)" }}>Rate:</strong> {rule.rate}
        </div>
        <div style={{ fontSize: "12px", lineHeight: 1.7, color: "rgba(19,18,16,.7)" }}>
          <strong style={{ color: "var(--o900)" }}>How the period is counted:</strong> {rule.periodBasis}
        </div>
        {rule.capsAndFloors ? (
          <div style={{ fontSize: "12px", lineHeight: 1.7, color: "rgba(19,18,16,.7)" }}>
            <strong style={{ color: "var(--o900)" }}>Caps &amp; tolerances:</strong> {rule.capsAndFloors}
          </div>
        ) : null}
        <div style={{ fontSize: "12px", lineHeight: 1.7, color: "rgba(19,18,16,.7)" }}>
          <strong style={{ color: "var(--o900)" }}>Exceptions:</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
            {rule.exceptions.map((e, i) => (
              <li key={i} style={{ marginBottom: "5px" }}>
                {e}
              </li>
            ))}
          </ul>
        </div>
        <div style={{ fontSize: "11px", color: "rgba(19,18,16,.5)" }}>
          Source:{" "}
          <a
            href={rule.sourceUrl}
            rel="noopener noreferrer"
            style={{ color: "var(--cu)", textDecoration: "none", wordBreak: "break-all" }}
            target="_blank"
          >
            {rule.sourceUrl}
          </a>
        </div>
      </div>
    </div>
  );
}

function ResultColumn({
  heading,
  result,
  rule,
  decimals,
  children,
}: {
  heading: string;
  result: ComputeResult;
  rule: InterestRule;
  decimals?: number;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid var(--b200)" }}>
        <TotalCard decimals={decimals} heading={heading} result={result} />
        <BreakdownTable decimals={decimals} result={result} />
      </div>
      <Notes notes={result.notes} />
      {children}
      <RulePanel rule={rule} />
    </div>
  );
}

// ── Tab: 234A ────────────────────────────────────────────────────────────────

function Tab234A() {
  const [tax, setTax] = useState("500000");
  const [advance, setAdvance] = useState("100000");
  const [tds, setTds] = useState("150000");
  const [relief, setRelief] = useState("0");
  const [mat, setMat] = useState("0");
  const [saTax, setSaTax] = useState("0");
  const [due, setDue] = useState("2025-09-16");
  const [filed, setFiled] = useState("2025-12-23");

  const result = useMemo(
    () =>
      compute234A({
        taxOnTotalIncome: num(tax),
        advanceTaxPaid: num(advance),
        tdsTcs: num(tds),
        relief: num(relief),
        matAmtCredit: num(mat),
        selfAssessmentPaidByDue: num(saTax),
        dueDateISO: due,
        filingDateISO: filed,
      }),
    [tax, advance, tds, relief, mat, saTax, due, filed],
  );

  return (
    <div className="icalc-grid">
      <div>
        <MoneyField label="Tax on total income (₹)" value={tax} onChange={setTax} />
        <MoneyField label="Advance tax paid (₹)" value={advance} onChange={setAdvance} />
        <MoneyField label="TDS / TCS (₹)" value={tds} onChange={setTds} />
        <MoneyField label="Relief u/s 89 / 90 / 90A / 91 (₹)" value={relief} onChange={setRelief} />
        <MoneyField label="MAT / AMT credit (₹)" value={mat} onChange={setMat} />
        <MoneyField label="Self-assessment tax paid on or before the due date (₹)" value={saTax} onChange={setSaTax} />
        <DateField label="Return due date (incl. any CBDT extension)" value={due} onChange={setDue} />
        <DateField label="Date return actually furnished" value={filed} onChange={setFiled} />
      </div>
      <ResultColumn decimals={0} heading="Interest u/s 234A" result={result} rule={getRule("234a")} />
    </div>
  );
}

// ── Tab: 234B ────────────────────────────────────────────────────────────────

function Tab234B() {
  const [tax, setTax] = useState("300000");
  const [tds, setTds] = useState("50000");
  const [relief, setRelief] = useState("0");
  const [mat, setMat] = useState("0");
  const [advance, setAdvance] = useState("200000");
  const [ayStart, setAyStart] = useState("2026-04-01");
  const [end, setEnd] = useState("2026-07-31");

  const result = useMemo(
    () =>
      compute234B({
        taxOnTotalIncome: num(tax),
        tdsTcs: num(tds),
        relief: num(relief),
        matAmtCredit: num(mat),
        advanceTaxPaid: num(advance),
        ayStartISO: ayStart,
        endISO: end,
      }),
    [tax, tds, relief, mat, advance, ayStart, end],
  );

  return (
    <div className="icalc-grid">
      <div>
        <MoneyField label="Tax on total income (₹)" value={tax} onChange={setTax} />
        <MoneyField label="TDS / TCS (₹)" value={tds} onChange={setTds} />
        <MoneyField label="Relief u/s 89 / 90 / 90A / 91 (₹)" value={relief} onChange={setRelief} />
        <MoneyField label="MAT / AMT credit (₹)" value={mat} onChange={setMat} />
        <MoneyField label="Advance tax paid during the FY (₹)" value={advance} onChange={setAdvance} />
        <DateField label="1 April of the assessment year" value={ayStart} onChange={setAyStart} />
        <DateField label="End date (143(1) determination / self-assessment tax payment)" value={end} onChange={setEnd} />
      </div>
      <ResultColumn decimals={0} heading="Interest u/s 234B" result={result} rule={getRule("234b")} />
    </div>
  );
}

// ── Tab: 234C ────────────────────────────────────────────────────────────────

function Tab234C() {
  const [tax, setTax] = useState("100000");
  const [presumptive, setPresumptive] = useState<"regular" | "presumptive">("regular");
  const [q1, setQ1] = useState("10000");
  const [q2, setQ2] = useState("30000");
  const [q3, setQ3] = useState("60000");
  const [q4, setQ4] = useState("90000");

  const result: Result234C = useMemo(
    () =>
      compute234C({
        taxDueOnReturnedIncome: num(tax),
        paidByJun15: num(q1),
        paidBySep15: num(q2),
        paidByDec15: num(q3),
        paidByMar15: num(q4),
        presumptive: presumptive === "presumptive",
      }),
    [tax, q1, q2, q3, q4, presumptive],
  );

  const rule = getRule("234c");

  return (
    <div>
      <div className="icalc-grid">
        <div>
          <label style={lbl}>Assessee type</label>
          <Segment
            options={[
              { key: "regular", label: "Regular (4 instalments)" },
              { key: "presumptive", label: "44AD/44ADA (single, 15 Mar)" },
            ]}
            value={presumptive}
            onChange={setPresumptive}
          />
          <MoneyField label="Tax due on returned income, net of TDS/TCS and reliefs (₹)" value={tax} onChange={setTax} />
          {presumptive === "regular" ? (
            <>
              <MoneyField label="Cumulative advance tax paid by 15 June (₹)" value={q1} onChange={setQ1} />
              <MoneyField label="Cumulative paid by 15 September (₹)" value={q2} onChange={setQ2} />
              <MoneyField label="Cumulative paid by 15 December (₹)" value={q3} onChange={setQ3} />
            </>
          ) : null}
          <MoneyField label="Cumulative paid by 15 March (₹)" value={q4} onChange={setQ4} />
        </div>

        <div>
          <div style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid var(--b200)" }}>
            <TotalCard decimals={0} heading="Interest u/s 234C" result={result} />
            {/* Quarterly 15/45/75/100 grid */}
            <div style={{ overflowX: "auto", borderTop: "none" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "640px" }}>
                <thead>
                  <tr style={{ background: "var(--b100)" }}>
                    {["Instalment", "Cumulative due", "Required (₹)", "Paid (₹)", "Shortfall (₹100 floor)", "Months", "Interest"].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === "Instalment" ? "left" : "right",
                          padding: "9px 12px",
                          fontSize: "10px",
                          letterSpacing: ".1em",
                          textTransform: "uppercase",
                          color: "rgba(19,18,16,.5)",
                          fontWeight: 500,
                          borderBottom: "1px solid var(--b200)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.quarters.map((q) => (
                    <tr key={q.instalment}>
                      <td style={{ padding: "10px 12px", fontSize: "12px", color: "var(--o900)", borderBottom: "1px solid var(--b200)" }}>
                        {q.instalment}
                        {q.toleranceNote ? (
                          <div style={{ fontSize: "10px", color: q.toleranceMet ? "var(--cu)" : "rgba(19,18,16,.45)", marginTop: "3px", lineHeight: 1.5 }}>
                            {q.toleranceNote}
                            {q.toleranceMet ? " — met" : " — not met"}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: "12px", textAlign: "right", color: "rgba(19,18,16,.6)", borderBottom: "1px solid var(--b200)", fontVariantNumeric: "tabular-nums" }}>
                        {q.requiredPct}%
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: "12px", textAlign: "right", color: "rgba(19,18,16,.6)", borderBottom: "1px solid var(--b200)", fontVariantNumeric: "tabular-nums" }}>
                        {inr(q.requiredAmt, 0)}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: "12px", textAlign: "right", color: "rgba(19,18,16,.6)", borderBottom: "1px solid var(--b200)", fontVariantNumeric: "tabular-nums" }}>
                        {inr(q.paid, 0)}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: "12px", textAlign: "right", color: "rgba(19,18,16,.6)", borderBottom: "1px solid var(--b200)", fontVariantNumeric: "tabular-nums" }}>
                        {q.toleranceMet ? "—" : inr(q.shortfallBase, 0)}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: "12px", textAlign: "right", color: "rgba(19,18,16,.6)", borderBottom: "1px solid var(--b200)", fontVariantNumeric: "tabular-nums" }}>
                        {q.toleranceMet || q.shortfallBase === 0 ? "—" : q.months}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: "13px", textAlign: "right", color: "var(--o900)", borderBottom: "1px solid var(--b200)", fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
                        {inr(q.interest, 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Notes notes={result.notes} />
          <RulePanel rule={rule} />
        </div>
      </div>
    </div>
  );
}

// ── Tab: GST interest (s.50) ─────────────────────────────────────────────────

function TabGstInterest() {
  const [head, setHead] = useState<"s50_1" | "s50_3">("s50_1");
  // s.50(1)
  const [cash, setCash] = useState("100000");
  const [itc, setItc] = useState("0");
  const [due1, setDue1] = useState("2026-04-20");
  const [paid1, setPaid1] = useState("2026-05-20");
  const [postProc, setPostProc] = useState(false);
  // s.50(3)
  const [wrongItc, setWrongItc] = useState("50000");
  const [utilised, setUtilised] = useState("2026-01-10");
  const [reversed, setReversed] = useState("2026-03-11");
  const [neverUtilised, setNeverUtilised] = useState(false);

  const result = useMemo(
    () =>
      head === "s50_1"
        ? computeGst50_1({
            cashLedgerTax: num(cash),
            itcSetOff: num(itc),
            dueDateISO: due1,
            paymentDateISO: paid1,
            postProceedings: postProc,
          })
        : computeGst50_3({
            wronglyAvailedItc: num(wrongItc),
            utilisationDateISO: utilised,
            reversalDateISO: reversed,
            neverUtilised,
          }),
    [head, cash, itc, due1, paid1, postProc, wrongItc, utilised, reversed, neverUtilised],
  );

  return (
    <div className="icalc-grid">
      <div>
        <label style={lbl}>Head of interest</label>
        <Segment
          options={[
            { key: "s50_1", label: "s.50(1) — tax paid late" },
            { key: "s50_3", label: "s.50(3) — wrongly utilised ITC" },
          ]}
          value={head}
          onChange={setHead}
        />
        {head === "s50_1" ? (
          <>
            <MoneyField label="Tax paid through the electronic CASH ledger (₹)" value={cash} onChange={setCash} />
            <MoneyField label="Tax settled by ITC set-off (₹)" value={itc} onChange={setItc} />
            <DateField label="Return due date" value={due1} onChange={setDue1} />
            <DateField label="Date tax actually paid" value={paid1} onChange={setPaid1} />
            <CheckField
              checked={postProc}
              label="Return filed after s.73 / s.74 / s.74A proceedings commenced (net-cash benefit lost — interest on gross liability)"
              onChange={setPostProc}
            />
          </>
        ) : (
          <>
            <MoneyField label="Wrongly availed ITC (₹)" value={wrongItc} onChange={setWrongItc} />
            <DateField label="Date the wrongly availed ITC was utilised" value={utilised} onChange={setUtilised} />
            <DateField label="Date of reversal / payment" value={reversed} onChange={setReversed} />
            <CheckField
              checked={neverUtilised}
              label="Credit-ledger balance never fell below the wrongly availed amount (never utilised — no interest)"
              onChange={setNeverUtilised}
            />
          </>
        )}
      </div>
      <div>
        <ResultColumn
          decimals={2}
          heading={head === "s50_1" ? "Interest u/s 50(1), CGST Act" : "Interest u/s 50(3), CGST Act"}
          result={result}
          rule={getRule("gst_50")}
        />
        {head === "s50_3" && (
          <p style={{ fontSize: "12px", color: "rgba(19,18,16,.55)", lineHeight: 1.6, marginTop: "10px" }}>
            Day-count endpoint pending confirmation against Rule 88B(3) — whether
            the utilisation day itself counts is not settled in the sources this
            page relies on, so the result may vary by one day’s interest.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Tab: GST late fee (s.47) ─────────────────────────────────────────────────

function TabGstLateFee() {
  const [returnType, setReturnType] = useState<"GSTR-3B" | "GSTR-1">("GSTR-3B");
  const [nil, setNil] = useState<"normal" | "nil">("normal");
  const [band, setBand] = useState<AatoBand>("upto1_5cr");
  const [due, setDue] = useState("2026-01-20");
  const [filed, setFiled] = useState("2026-04-30");

  const result = useMemo(
    () =>
      computeGstLateFee({
        returnType,
        nilReturn: nil === "nil",
        aatoBand: band,
        dueDateISO: due,
        filingDateISO: filed,
      }),
    [returnType, nil, band, due, filed],
  );

  return (
    <div className="icalc-grid">
      <div>
        <label style={lbl}>Return</label>
        <Segment
          options={[
            { key: "GSTR-3B", label: "GSTR-3B" },
            { key: "GSTR-1", label: "GSTR-1" },
          ]}
          value={returnType}
          onChange={setReturnType}
        />
        <label style={lbl}>Return content</label>
        <Segment
          options={[
            { key: "normal", label: "Normal (₹50/day)" },
            { key: "nil", label: "NIL return (₹20/day)" },
          ]}
          value={nil}
          onChange={setNil}
        />
        {nil === "normal" ? (
          <div>
            <label htmlFor="gst-late-fee-aato" style={lbl}>Aggregate turnover, preceding FY (sets the cap)</label>
            <select id="gst-late-fee-aato" style={inputStyle} value={band} onChange={(e) => setBand(e.target.value as AatoBand)}>
              {AATO_BANDS.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.label} — cap {inr(b.cap, 0)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <DateField label="Return due date" value={due} onChange={setDue} />
        <DateField label="Date return actually filed" value={filed} onChange={setFiled} />
      </div>
      <ResultColumn decimals={0} heading={`Late fee — ${returnType} (CGST + SGST)`} result={result} rule={getRule("gst_late_fee")}>
        {!result.nil ? (
          <p style={{ fontSize: "12px", color: "rgba(19,18,16,.6)", marginTop: "12px", lineHeight: 1.7 }}>
            Split: {inr(result.total / 2, 0)} CGST + {inr(result.total / 2, 0)} SGST. No late fee under the IGST Act.
          </p>
        ) : null}
      </ResultColumn>
    </div>
  );
}

// ── Tab: TDS 201(1A) ─────────────────────────────────────────────────────────

function TabTds201() {
  const [amount, setAmount] = useState("10000");
  const [deductible, setDeductible] = useState("2026-05-21");
  const [deducted, setDeducted] = useState("2026-05-21");
  const [deposited, setDeposited] = useState("2026-06-08");

  const result = useMemo(
    () =>
      computeTds201({
        tdsAmount: num(amount),
        deductibleDateISO: deductible,
        deductionDateISO: deducted,
        depositDateISO: deposited,
      }),
    [amount, deductible, deducted, deposited],
  );

  const dueDate = tdsDepositDueDate(deducted);

  return (
    <div className="icalc-grid">
      <div>
        <MoneyField label="TDS amount (₹)" value={amount} onChange={setAmount} />
        <DateField label="Date tax was deductible (payment / credit, whichever earlier)" value={deductible} onChange={setDeductible} />
        <DateField label="Date tax actually deducted" value={deducted} onChange={setDeducted} />
        <DateField label="Date deposited with the government" value={deposited} onChange={setDeposited} />
        {dueDate ? (
          <p style={{ fontSize: "12px", color: "rgba(19,18,16,.55)", lineHeight: 1.7, marginTop: "-6px" }}>
            Deposit due date for this deduction: <strong style={{ color: "var(--o900)" }}>{dueDate}</strong> (7th of the
            following month; 30 April for March deductions).
          </p>
        ) : null}
      </div>
      <ResultColumn decimals={0} heading="Interest u/s 201(1A)" result={result} rule={getRule("tds_201_1a")} />
    </div>
  );
}

// ── Tab: 234E ────────────────────────────────────────────────────────────────

function Tab234E() {
  const [amount, setAmount] = useState("4800");
  const [due, setDue] = useState("2026-07-31");
  const [filed, setFiled] = useState("2026-09-29");

  const result = useMemo(
    () =>
      compute234E({
        tdsInStatement: num(amount),
        dueDateISO: due,
        filingDateISO: filed,
      }),
    [amount, due, filed],
  );

  return (
    <div className="icalc-grid">
      <div>
        <MoneyField label="Total TDS/TCS in the statement (₹) — this is the cap" value={amount} onChange={setAmount} />
        <DateField label="Statement due date (31 Jul / 31 Oct / 31 Jan / 31 May for TDS Q1–Q4)" value={due} onChange={setDue} />
        <DateField label="Date statement actually furnished" value={filed} onChange={setFiled} />
      </div>
      <ResultColumn decimals={0} heading="Fee u/s 234E" result={result} rule={getRule("tds_234e")} />
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function InterestCalculatorPage() {
  const [tab, setTab] = useState<TabKey>("234a");

  return (
    <PageShell>
      <style>{`
        .icalc-grid{display:grid;grid-template-columns:minmax(260px,380px) 1fr;gap:36px;align-items:start}
        @media (max-width: 760px){.icalc-grid{grid-template-columns:1fr;gap:24px}}
      `}</style>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        className="simple-hero"
        style={{ padding: "72px clamp(20px,4vw,52px) 60px", borderBottom: "1px solid var(--b200)" }}
      >
        <a href="/tools" style={{ fontSize: "12px", color: "rgba(19,18,16,.55)", textDecoration: "none", letterSpacing: ".05em" }}>
          ← All tools
        </a>
        <div className="ey rev" style={{ marginTop: "20px" }}>Tax / Interest &amp; Late Fees</div>
        <h1 className="sh2 rev d1" style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "clamp(32px,4vw,52px)" }}>
          Interest &amp; late-fee calculator.{" "}<br />
          <em>234A · 234B · 234C · GST · TDS.</em>
        </h1>
        <p className="rev d2" style={{ fontSize: "15px", color: "rgba(19,18,16,.55)", maxWidth: "620px", marginTop: "14px", lineHeight: 1.8 }}>
          Statutory interest and late fees, computed step by step with the formula shown — income-tax interest under
          234A/234B/234C, GST interest under CGST s.50, GST late fees under s.47, TDS interest under 201(1A), and the
          234E filing fee. Every rule carries its 1961-Act citation and its Income-tax Act, 2025 equivalent.
        </p>
        <div
          style={{
            marginTop: "20px",
            fontSize: "11px",
            letterSpacing: ".04em",
            color: "rgba(19,18,16,.5)",
            lineHeight: 1.7,
          }}
        >
          Last reviewed: {REVIEWED_ON} · runs in your browser, nothing uploaded
        </div>
      </section>

      {/* ── Disclaimer (prominent, above the calculator) ─────────────── */}
      <section
        style={{
          padding: "36px clamp(20px,4vw,52px)",
          borderBottom: "1px solid var(--b200)",
          background: "var(--b100)",
        }}
      >
        <p style={{ fontSize: "12px", color: "rgba(19,18,16,.6)", lineHeight: 1.8, maxWidth: "860px" }}>
          <strong style={{ color: "var(--o900)" }}>
            These are estimates on the statutory formula, not tax advice.
          </strong>{" "}
          CBDT due-date extensions, department orders, portal-computed figures, s.234B(2) mid-way payments, the s.234C
          income-driven carve-outs, GST amnesty notifications and other edge cases all change outcomes. The figures the
          department or the GST portal computes prevail over anything shown here. Verify against the bare Act and the
          notifications cited beside each result, and take advice on your own facts before paying or contesting a
          demand. Nothing on this page creates an adviser–client relationship. Last reviewed: {REVIEWED_ON}.
        </p>
      </section>

      {/* ── Calculator ───────────────────────────────────────────────── */}
      <section style={{ padding: "36px clamp(20px,4vw,52px) 52px", borderBottom: "1px solid var(--b200)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "30px" }}>
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                style={{
                  padding: "9px 14px",
                  background: active ? "var(--cu)" : "var(--b50)",
                  color: active ? "var(--b50)" : "var(--o900)",
                  border: `1px solid ${active ? "var(--cu)" : "var(--b200)"}`,
                  borderRadius: "3px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontFamily: "var(--font-dm), sans-serif",
                  transition: "background .18s, color .18s, border-color .18s",
                }}
                type="button"
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "234a" ? <Tab234A /> : null}
        {tab === "234b" ? <Tab234B /> : null}
        {tab === "234c" ? <Tab234C /> : null}
        {tab === "gst_interest" ? <TabGstInterest /> : null}
        {tab === "gst_late_fee" ? <TabGstLateFee /> : null}
        {tab === "tds_201" ? <TabTds201 /> : null}
        {tab === "234e" ? <Tab234E /> : null}
      </section>

      {/* ── Reference: the seven rules at a glance ───────────────────── */}
      <section style={{ padding: "52px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)", background: "var(--b100)" }}>
        <div className="ey">Reference</div>
        <h2 className="sh2" style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "clamp(22px,2.4vw,30px)", marginBottom: "20px" }}>
          The seven levies, side by side.
        </h2>
        <div style={{ overflowX: "auto", border: "1px solid var(--b200)", borderRadius: "6px", background: "var(--b50)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px" }}>
            <thead>
              <tr style={{ background: "var(--b100)" }}>
                {["Levy", "Rate", "Period basis", "1961-Act / GST citation", "2025-Act / current"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "10px 14px",
                      fontSize: "10px",
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      color: "rgba(19,18,16,.5)",
                      fontWeight: 500,
                      borderBottom: "1px solid var(--b200)",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ["234a", "1%/month or part", "Month or part (Rule 119A) — due date → filing", "s.234A, IT Act 1961", "s.423, IT Act 2025"],
                  ["234b", "1%/month or part", "Month or part — 1 April of AY onwards", "s.234B, IT Act 1961", "s.424, IT Act 2025"],
                  ["234c", "1%/month", "Fixed 3 / 3 / 3 / 1 months on each shortfall", "s.234C, IT Act 1961", "s.425, IT Act 2025"],
                  ["gst_50", "18% p.a.", "Per day × days ÷ 365 (Rule 88B)", "CGST s.50 + Notif. 13/2017, 16/2021, 09/2022", "Unchanged — GST untouched by IT Act 2025"],
                  ["gst_late_fee", "₹50/day (₹20 NIL)", "Per calendar day, capped per return", "CGST s.47 + Notif. 19/2021 & 20/2021", "Unchanged"],
                  ["tds_201_1a", "1% / 1.5% per month or part", "Deductible→deduction @1%; deduction→deposit @1.5%", "s.201(1A), IT Act 1961", "s.398, IT Act 2025"],
                  ["tds_234e", "₹200/day", "Per calendar day, capped at TDS in statement", "s.234E, IT Act 1961", "s.427, IT Act 2025"],
                ] as Array<[string, string, string, string, string]>
              ).map((r) => (
                <tr key={r[0]}>
                  <td style={{ padding: "10px 14px", fontSize: "12px", color: "var(--o900)", borderBottom: "1px solid var(--b200)", fontWeight: 500, whiteSpace: "nowrap" }}>
                    {getRule(r[0] as Parameters<typeof getRule>[0]).title.split("(")[0].trim()}
                  </td>
                  {r.slice(1).map((c, i) => (
                    <td key={i} style={{ padding: "10px 14px", fontSize: "12px", color: "rgba(19,18,16,.65)", borderBottom: "1px solid var(--b200)", lineHeight: 1.6 }}>
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: "12px", color: "rgba(19,18,16,.5)", lineHeight: 1.75, marginTop: "18px", maxWidth: "860px" }}>
          The 2025-Act section numbers take effect for tax year 2026-27 (from 1 April 2026); the mechanics — rates,
          period bases, tolerances and caps — carry over unchanged. Rule 119A rounding (part-month up to a full month,
          interest base down to the nearest ₹100) applies to the income-tax interest provisions only, never to GST.
        </p>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section style={{ padding: "60px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)" }}>
        <div style={{ maxWidth: "780px", margin: "0 auto" }}>
          <h2 style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "clamp(24px,2.8vw,34px)", fontWeight: 200, color: "var(--o900)", marginBottom: "32px", letterSpacing: "-.01em" }}>
            Frequently asked
          </h2>
          {FAQS.map((f, i) => (
            <details key={i} style={{ borderTop: i === 0 ? "1px solid var(--b300)" : "none", borderBottom: "1px solid var(--b300)", padding: "18px 0" }}>
              <summary style={{ fontFamily: "var(--font-dm), sans-serif", fontSize: "15px", fontWeight: 500, color: "var(--o900)", cursor: "pointer", listStyle: "none" }}>
                {f.q}
              </summary>
              <p style={{ fontSize: "14px", color: "rgba(19,18,16,.7)", lineHeight: 1.8, marginTop: "12px" }}>{f.a}</p>
            </details>
          ))}
          <p style={{ fontSize: "12px", color: "rgba(19,18,16,.5)", lineHeight: 1.75, marginTop: "22px" }}>
            Last reviewed: {REVIEWED_ON}.
          </p>
        </div>
      </section>

      <FaqJsonLd faqs={FAQS} />
      <ReviewedPageSchema
        description="Interest and late-fee computation under Indian tax and company law, with the rule behind each figure."
        name="Statutory interest calculator — India"
        path="/tools/interest-calculator"
        reviewedOn={REVIEWED_ON}
      />

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section style={{ padding: "72px clamp(20px,4vw,52px)", textAlign: "center", background: "var(--o950)" }}>
        <h2 style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "clamp(26px,3.2vw,38px)", fontWeight: 200, color: "var(--b50)", marginBottom: "14px" }}>
          Want the deadline caught before the interest starts?{" "}<br />
          <em style={{ color: "var(--cl)", fontStyle: "italic" }}>Try LexVio Tax AI.</em>
        </h2>
        <a className="cta-p" data-mag="true" href="/product/tax-ai">
          <span className="cta-p-t">See Tax AI →</span>
        </a>
      </section>
    </PageShell>
  );
}
